/** Hotkey matching, with no reference to the DOM so it can be tested headless. */

/** The parts of a KeyboardEvent this package reads. */
export interface KeyEventLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly metaKey: boolean;
  readonly repeat: boolean;
}

export interface ParsedCombo {
  readonly key: string;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
  readonly meta: boolean;
}

/**
 * `"ctrl+shift+e"`, `"space"`, `"ArrowLeft"`. Modifiers in any order; the last
 * segment is the key. Comparison is case-insensitive because `event.key` for a
 * shifted letter is upper case and a combo written `"shift+e"` should still fire.
 */
export function parseCombo(combo: string): ParsedCombo {
  const parts = combo.split("+").map((p) => p.trim()).filter((p) => p.length > 0);
  let ctrl = false, alt = false, shift = false, meta = false;
  let key = "";
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (lower === "ctrl" || lower === "control") ctrl = true;
    else if (lower === "alt" || lower === "option") alt = true;
    else if (lower === "shift") shift = true;
    else if (lower === "meta" || lower === "cmd" || lower === "super") meta = true;
    else key = lower;
  }
  return { key: normalizeKey(key), ctrl, alt, shift, meta };
}

/** Aliases for keys whose `event.key` is awkward to write in a combo string. */
export function normalizeKey(key: string): string {
  const k = key.toLowerCase();
  switch (k) {
    case " ":
    case "spacebar":
      return "space";
    // The zoom keys. `+` is Shift+`=` on a US layout and `+` on a numpad, and
    // a combo string cannot name it anyway (`+` is the modifier separator),
    // so the plus key answers to the `=` combo; the numpad's `-` is the same
    // key as the row's. Old browsers spelled the numpad keys "add" and
    // "subtract".
    case "+":
    case "add":
      return "=";
    case "subtract":
      return "-";
    case "esc":
      return "escape";
    case "left": return "arrowleft";
    case "right": return "arrowright";
    case "up": return "arrowup";
    case "down": return "arrowdown";
    default:
      return k;
  }
}

export function matchesCombo(event: KeyEventLike, combo: ParsedCombo): boolean {
  if (normalizeKey(event.key) !== combo.key) return false;
  if (event.ctrlKey !== combo.ctrl) return false;
  if (event.altKey !== combo.alt) return false;
  if (event.metaKey !== combo.meta) return false;
  // A combo that does not name shift still fires when shift is held, because a
  // shifted letter arrives as a different `key` anyway and arrow+shift is a
  // common accidental hold. A combo that *does* name shift requires it.
  if (combo.shift && !event.shiftKey) return false;
  return true;
}

/**
 * True when the event came from somewhere the player is typing. A hotkey must
 * never eat a keystroke aimed at a text field.
 */
export function isTypingTarget(tag: string | null, editable: boolean): boolean {
  if (editable) return true;
  if (tag === null) return false;
  const t = tag.toLowerCase();
  return t === "input" || t === "textarea" || t === "select";
}
